import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, fmt$, fmtPct, fmtN } from "../lib/appCommon";
import { fastGet } from "../lib/fastFetch";
import RealTradePreviewModal from "./RealTradePreviewModal";

// ─── Autopilot Panel (Full 10-Stage Pipeline) ────────────────────────────────
const AP_SCAN_CACHE_KEY = "omega_last_scan_v2";
function loadCachedScan() {
  try {
    const raw = localStorage.getItem(AP_SCAN_CACHE_KEY);
    if (!raw) return [];
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts < 10 * 60 * 1000) return data; // 10-min TTL
  } catch {}
  return [];
}

function AutopilotPanel({ apMode, setApMode, ziThreshold, setZiThreshold, setSelectedOption }) {
  const [scanResults, setScanResults] = useState(loadCachedScan);
  const [scanning, setScanning] = useState(false);
  const [performance, setPerformance] = useState(null);
  const [positions, setPositions] = useState([]);
  const [pdt, setPdt] = useState(null);

  useEffect(() => {
    // fastGet = show cached value instantly, revalidate in background
    const u1 = fastGet(`${API}/autopilot/performance`, setPerformance);
    const u2 = fastGet(`${API}/autopilot/positions`, d => setPositions(Array.isArray(d) ? d : []));
    const u3 = fastGet(`${API}/pdt/status`, setPdt);
    return () => { u1(); u2(); u3(); };
  }, []);

  const [strategySwitched, setStrategySwitched] = useState(false);

  const runFullScan = async () => {
    setScanning(true);
    setStrategySwitched(false);
    try {
      const r = await axios.post(`${API}/autopilot/scan`, {
        tickers: ["AAL", "AAPL", "SPY", "QQQ", "NVDA", "TSLA", "AMZN", "MSFT"],
        mode: apMode,
        threshold: ziThreshold,
      });
      const results = r.data?.results || [];
      setScanResults(results);
      try { localStorage.setItem(AP_SCAN_CACHE_KEY, JSON.stringify({ data: results, ts: Date.now() })); } catch {}
      if (r.data?.strategySwitched) setStrategySwitched(true);
    } catch {} finally { setScanning(false); }
  };

  const [previewTrade, setPreviewTrade] = useState(null);

  const executeDecision = async (result) => {
    // Open the Real Trade Preview Gate modal instead of firing directly
    setPreviewTrade(result);
  };

  const modes = [
    { id: "OFF",            label: "Off",         desc: "No scans, no trades, fully paused",              tag: "t-bear", tagText: "Inactive",    color: "#6b7280" },
    { id: "OBSERVE_ONLY",  label: "Observe",      desc: "Scan + grade signals, no execution",             tag: "t-blue", tagText: "Watch only",  color: "#60a5fa" },
    { id: "ASSISTED",      label: "Assisted",     desc: "AI sizes trades, you confirm each one · Zi ≥ 65", tag: "t-neu",  tagText: "Human loop",  color: "#a78bfa" },
    { id: "GEM_ONLY",      label: "Gem Only",     desc: "Auto-execute A+ gems · Zi ≥ 85 · Meta ≥ 0.80",  tag: "t-bull", tagText: "Auto gems",   color: "#4ade80" },
    { id: "DEFENSE",       label: "Defense",      desc: "Exits and stops only — no new entries",          tag: "t-neu",  tagText: "Exits only",  color: "#fbbf24" },
    { id: "FULL_AUTO",     label: "Full Auto",    desc: "Full AI execution across all trade types · Zi ≥ 75", tag: "t-bull", tagText: "Autonomous", color: "#f87171" },
    { id: "LIQUIDATE_RISK",label: "Liquidate",    desc: "Immediately close all losing positions",         tag: "t-bear", tagText: "Emergency",   color: "#ef4444" },
  ];

  const ziClassColor = (cls) => cls === "ULTRA_GEM" ? "var(--green)" : cls === "GEM" ? "var(--blue)" : cls === "NEUTRAL" ? "var(--yellow)" : "var(--red)";

  return (
    <div data-testid="autopilot-panel">
      {/* Mode Selection */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div className="sh" style={{ margin: 0 }}>Automation Mode</div>
        <span style={{ fontSize: 10, fontWeight: 800, padding: "3px 10px", borderRadius: 8,
          background: `${modes.find(m => m.id === apMode)?.color || "#6b7280"}15`,
          color: modes.find(m => m.id === apMode)?.color || "#6b7280",
          border: `1px solid ${modes.find(m => m.id === apMode)?.color || "#6b7280"}30` }}>
          {modes.find(m => m.id === apMode)?.label || apMode}
        </span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 8, marginBottom: 14 }}>
        {modes.map(m => {
          const isActive = apMode === m.id;
          return (
            <div key={m.id} onClick={() => setApMode(m.id)}
              style={{ padding: "11px 12px", borderRadius: 12, cursor: "pointer", transition: "all 0.15s",
                background: isActive ? `${m.color}12` : "var(--layer2)",
                border: `${isActive ? "2px" : "1px"} solid ${isActive ? `${m.color}50` : "var(--border)"}`,
              }}
              onMouseEnter={e => { if (!isActive) { e.currentTarget.style.borderColor = `${m.color}30`; e.currentTarget.style.background = `${m.color}06`; } }}
              onMouseLeave={e => { if (!isActive) { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.background = "var(--layer2)"; } }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: m.color, flexShrink: 0, boxShadow: isActive ? `0 0 8px ${m.color}` : "none" }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: isActive ? m.color : "var(--text)" }}>{m.label}</span>
              </div>
              <span className={`tag ${m.tag}`} style={{ fontSize: 8, marginBottom: 5, display: "inline-block" }}>{m.tagText}</span>
              <div style={{ fontSize: 9, color: "var(--muted)", lineHeight: 1.5 }}>{m.desc}</div>
            </div>
          );
        })}
      </div>

      {/* Threshold + Risk Gate Config */}
      <div className="grid2" style={{ marginBottom: 14 }}>
        <div className="card">
          <div className="sh">Zi Score Threshold</div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 28, fontWeight: 700, color: ziThreshold >= 68 ? "var(--green)" : ziThreshold >= 53 ? "var(--blue)" : "var(--yellow)" }}>{ziThreshold}</span>
            <input type="range" min="40" max="95" value={ziThreshold} onChange={e => setZiThreshold(Number(e.target.value))} style={{ flex: 1, accentColor: "#3b82f6" }} />
          </div>
          <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 4 }}>
            {ziThreshold >= 68 ? "ULTRA GEM only — highest conviction" : ziThreshold >= 53 ? "GEM+ — high confidence trades" : "Broader scan — includes NEUTRAL"}
          </div>
        </div>
        <div className="card">
          <div className="sh">Risk Gate 5.0 — Quad Lock</div>
          <div style={{ fontSize: 11, color: "var(--muted)", lineHeight: 1.7 }}>
            <div><span style={{ color: "var(--green)" }}>Lock 1:</span> Max 2% account risk per trade (CVaR)</div>
            <div><span style={{ color: "var(--blue)" }}>Lock 2:</span> Max 25% sector concentration, max 3 ULTRA GEM</div>
            <div><span style={{ color: "var(--purple)" }}>Lock 3:</span> 6% weekly drawdown pause, 15% monthly review</div>
            <div><span style={{ color: "var(--yellow)" }}>Lock 4:</span> PDT compliance — max 3 day trades / 5 business days (under $25K)</div>
          </div>
        </div>

        {/* PDT Day Trade Counter */}
        {pdt && (
          <div className="card" style={{ borderColor: pdt.dayTradesRemaining <= 1 ? "rgba(248,113,113,0.3)" : "rgba(74,222,128,0.2)" }}>
            <div className="row">
              <div className="sh" style={{ margin: 0 }}>PDT Day Trade Counter</div>
              <span className={`tag ${pdt.pdtExempt ? "t-bull" : pdt.dayTradesRemaining <= 0 ? "t-bear" : pdt.dayTradesRemaining <= 1 ? "t-neu" : "t-bull"}`}>
                {pdt.pdtExempt ? "EXEMPT" : `${pdt.dayTradesRemaining} remaining`}
              </span>
            </div>
            <div className="grid4" style={{ marginTop: 8 }}>
              <div className="m-box">
                <div className="m-lbl">Day Trades Used</div>
                <div className="m-val" style={{ color: pdt.dayTradesUsed >= 3 ? "var(--red)" : "var(--green)" }}>{pdt.dayTradesUsed} / 3</div>
              </div>
              <div className="m-box"><div className="m-lbl">Strategy</div><div className="m-val" style={{ fontSize: 14 }}>{pdt.tradeStrategy === "day_trade" ? "Day Trade" : "Swing"}</div></div>
              <div className="m-box"><div className="m-lbl">Account Type</div><div className="m-val" style={{ fontSize: 14 }}>{pdt.accountType === "cash" ? "Cash" : "Margin"}</div></div>
              <div className="m-box"><div className="m-lbl">Equity</div><div className="m-val" style={{ fontSize: 14 }}>{fmt$(pdt.equity)}</div></div>
            </div>
            {pdt.pdtExempt && <div style={{ fontSize: 10, color: "var(--green)", marginTop: 6 }}>{pdt.exemptReason}</div>}
            {!pdt.pdtExempt && pdt.dayTradesRemaining <= 1 && (
              <div style={{ fontSize: 10, color: "var(--red)", marginTop: 6 }}>Warning: {pdt.dayTradesRemaining <= 0 ? "PDT limit reached — switch to Swing or wait 5 days" : "1 day trade remaining — use wisely for highest Zi Score only"}</div>
            )}
          </div>
        )}
      </div>

      {/* 10-Stage Pipeline */}
      <div className="card">
        <div className="row">
          <span className="sh">10-Stage Decision Pipeline</span>
          <button className="btn btn-blue" onClick={runFullScan} disabled={scanning} data-testid="run-scan">
            {scanning ? "Scanning..." : "Run Full Pipeline"}
          </button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 4 }}>
          {[
            { n: "1. Scan", d: "Public.com", active: true },
            { n: "2. Filter", d: "Liquidity", active: true },
            { n: "3. Macro", d: "Events", active: true },
            { n: "4. Tea", d: "Sentiment", active: true },
            { n: "5. IV", d: "Analysis", active: true },
            { n: "6. MC", d: "10K paths", active: true },
            { n: "7. Greeks", d: "Multi-TF", active: true },
            { n: "8. Zi 5.0", d: "Synthesis", active: true },
            { n: "9. Risk", d: "Triple Lock", active: true },
            { n: "10. Execute", d: apMode === "FULL_AUTO" || apMode === "GEM_ONLY" ? "Auto" : "Manual", active: apMode === "FULL_AUTO" || apMode === "GEM_ONLY" },
          ].map((s, i) => (
            <div key={i} className={`pipe-step ${s.active ? "active" : "standby"}`} style={{ padding: 6, fontSize: 10 }}>
              <div style={{ fontWeight: 600 }}>{s.n}</div>
              <div style={{ fontSize: 8, color: "var(--muted)", marginTop: 2 }}>{s.d}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Smart Strategy Switch Alert */}
      {strategySwitched && (
        <div data-testid="strategy-switch-alert" style={{ padding: "10px 14px", marginBottom: 10, borderRadius: 10, background: "rgba(250,204,21,0.08)", border: "1.5px solid rgba(250,204,21,0.3)", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 18 }}>&#9888;</span>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#facc15" }}>Smart Strategy Switch Activated</div>
            <div style={{ fontSize: 11, color: "var(--muted)" }}>Auto-switched from Day Trade to Swing mode — 2/3 PDT day trades used. Protecting you from PDT restrictions.</div>
          </div>
        </div>
      )}

      {/* Scan Results — Full Autopilot Cards */}
      {scanResults.length > 0 && (
        <div className="card">
          <div className="sh" style={{ display: "flex", alignItems: "center", gap: 6 }}>Pipeline Results ({scanResults.length} opportunities)</div>
          {scanResults.map((r, i) => (
            <div key={i} style={{ background: "var(--layer2)", borderRadius: 10, padding: 14, marginBottom: 10,
              border: `1.5px solid ${r.ziClass === "ULTRA_GEM" ? "rgba(74,222,128,0.4)" : r.ziClass === "GEM" ? "rgba(96,165,250,0.4)" : "var(--border)"}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 15, fontWeight: 700 }}>{r.ticker}</span>
                  <span style={{ fontSize: 13 }}>${r.strike} {r.optionType?.toUpperCase()}</span>
                  <span style={{ fontSize: 24, fontWeight: 700, color: ziClassColor(r.ziClass) }}>{r.ziScore}</span>
                  <span className={`tag ${r.ziClass === "ULTRA_GEM" ? "t-bull" : r.ziClass === "GEM" ? "t-blue" : "t-neu"}`}>{r.ziClass?.replace("_", " ")}</span>
                  {r.omegaVerdict && <span className={`tag ${r.omegaVerdict === "ULTRA_GEM" ? "t-bull" : r.omegaVerdict === "GEM" ? "t-blue" : r.omegaVerdict === "FORBIDDEN" ? "t-bear" : "t-neu"}`} style={{ fontSize: 8, opacity: 0.85 }}>Ω {r.omegaVerdict?.replace("_", " ")}</span>}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  {apMode !== "OBSERVE_ONLY" && apMode !== "OFF" && r.riskGate?.passed && (
                    <button className="btn-buy" style={{ fontSize: 10, padding: "6px 12px" }} onClick={() => executeDecision(r)}>Execute Trade</button>
                  )}
                  <button className="btn" style={{ fontSize: 10 }}
                    onClick={() => setSelectedOption({ ...r, underlying: r.price, symbol: r.ticker, expiry: r.expiry, type: r.optionType, bid: r.bid, ask: r.ask, volume: r.kelly?.dollars || 0, oi: 0 })}>
                    Analyze
                  </button>
                </div>
              </div>
              <div className="grid5" style={{ marginBottom: 6 }}>
                <div className="ph"><div className="ph-l">MC PoP</div><div style={{ fontSize: 14, fontWeight: 500, color: r.mcPoP > 0.6 ? "var(--green)" : "var(--yellow)" }}>{fmtPct(r.mcPoP * 100)}</div></div>
                <div className="ph"><div className="ph-l">Tea Score</div><div style={{ fontSize: 14, fontWeight: 500 }}>{r.teaScore ?? "—"}</div></div>
                <div className="ph"><div className="ph-l">IV</div><div style={{ fontSize: 14, fontWeight: 500 }}>{fmtPct(r.iv * 100)}</div></div>
                <div className="ph"><div className="ph-l">Kelly Size</div><div style={{ fontSize: 14, fontWeight: 500, color: "var(--green)" }}>{fmt$(r.kelly?.dollars)}</div></div>
                <div className="ph"><div className="ph-l">DTE</div><div style={{ fontSize: 14, fontWeight: 500 }}>{r.dte}</div></div>
              </div>
              <div style={{ display: "flex", gap: 12, fontSize: 10, color: "var(--muted)", flexWrap: "wrap" }}>
                <span>Mid: ${fmtN(r.mid, 2)}</span>
                <span>Break: ${fmtN(r.breakeven, 2)}</span>
                <span>Max Loss: {fmt$(r.maxLoss)}</span>
                <span>Risk Gate: <span style={{ color: r.riskGate?.passed ? "var(--green)" : "var(--red)" }}>{r.riskGate?.passed ? "PASS" : "BLOCKED"}</span></span>
                <span>Lock: {r.decision?.lockId}</span>
              </div>
              {r.confidence && (
                <div data-testid={`confidence-indicator-${i}`} style={{ marginTop: 8, padding: "6px 10px", borderRadius: 8,
                  background: r.confidence.paperTests > 10 ? "rgba(74,222,128,0.06)" : r.confidence.paperTests > 0 ? "rgba(250,204,21,0.06)" : "rgba(139,148,158,0.06)",
                  border: `1px solid ${r.confidence.paperTests > 10 ? "rgba(74,222,128,0.2)" : r.confidence.paperTests > 0 ? "rgba(250,204,21,0.2)" : "rgba(139,148,158,0.15)"}`,
                  display: "flex", alignItems: "center", gap: 8, fontSize: 10 }}>
                  <span style={{ fontWeight: 600, color: r.confidence.paperTests > 10 ? "var(--green)" : r.confidence.paperTests > 0 ? "#facc15" : "var(--dim)" }}>
                    {r.confidence.paperTests > 10 ? "HIGH CONFIDENCE" : r.confidence.paperTests > 0 ? "BUILDING" : "NEW"}
                  </span>
                  <span style={{ color: "var(--muted)" }}>{r.confidence.label}</span>
                  {r.confidence.paperTests > 0 && (
                    <span style={{ marginLeft: "auto", color: "var(--dim)" }}>{r.confidence.wins}W / {r.confidence.losses}L</span>
                  )}
                </div>
              )}
              {/* OCI v2 Intelligence Gate */}
              {r.ociGate && (
                <div style={{ marginTop: 8, padding: "8px 10px", borderRadius: 8, background: "rgba(167,139,250,0.06)", border: "1px solid rgba(167,139,250,0.2)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 9, fontWeight: 700, color: "#a78bfa" }}>◆ OCI v2</span>
                    <span style={{ fontSize: 10, fontWeight: 700,
                      color: r.ociGate.action === "BUY" ? "var(--green)" : r.ociGate.action === "BLOCK" ? "var(--red)" : r.ociGate.action === "WAIT" ? "#fbbf24" : "var(--muted)" }}>
                      {r.ociGate.action}
                    </span>
                    {r.ociGate.confidence != null && (
                      <span style={{ fontSize: 9, color: "var(--muted)" }}>{(r.ociGate.confidence * 100).toFixed(0)}% confidence</span>
                    )}
                    {r.ociGate.earnings_phase && r.ociGate.earnings_phase !== "NO_EARNINGS" && (
                      <span style={{ fontSize: 8, padding: "1px 6px", borderRadius: 3,
                        background: r.ociGate.earnings_phase === "EVENT_WINDOW" ? "rgba(248,113,113,0.12)" : "rgba(251,191,36,0.1)",
                        color: r.ociGate.earnings_phase === "EVENT_WINDOW" ? "#f87171" : "#fbbf24" }}>
                        {r.ociGate.earnings_phase.replace("_", " ")}
                      </span>
                    )}
                    {r.ociGate.iv_crush_pct != null && r.ociGate.iv_crush_pct > 0 && (
                      <span style={{ fontSize: 8, color: "#f87171", padding: "1px 6px", borderRadius: 3, background: "rgba(248,113,113,0.08)" }}>
                        IV crush ~{r.ociGate.iv_crush_pct.toFixed(0)}%
                      </span>
                    )}
                  </div>
                  {r.ociGate.ev_sizing && (
                    <div style={{ display: "flex", gap: 10, fontSize: 9, color: "var(--muted)", flexWrap: "wrap" }}>
                      <span>EV Mode: <strong style={{ color: r.ociGate.ev_sizing.mode === "BLOCK" ? "#f87171" : r.ociGate.ev_sizing.mode === "AGGRESSIVE" ? "#4ade80" : "#a78bfa" }}>{r.ociGate.ev_sizing.mode}</strong></span>
                      {r.ociGate.ev_sizing.suggested_contracts > 0 && (
                        <span>Contracts: <strong style={{ color: "var(--text)" }}>{r.ociGate.ev_sizing.suggested_contracts}</strong></span>
                      )}
                      {r.ociGate.ev_sizing.suggested_notional > 0 && (
                        <span>Notional: <strong style={{ color: "var(--text)" }}>${r.ociGate.ev_sizing.suggested_notional.toFixed(0)}</strong></span>
                      )}
                      {r.ociGate.ev_sizing.edge_score != null && (
                        <span>Edge: <strong style={{ color: "var(--text)" }}>{r.ociGate.ev_sizing.edge_score.toFixed(1)}</strong></span>
                      )}
                    </div>
                  )}
                  {r.ociGate.warnings?.length > 0 && (
                    <div style={{ marginTop: 4, display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {r.ociGate.warnings.map((w, wi) => (
                        <span key={wi} style={{ fontSize: 8, color: "#fbbf24", background: "rgba(251,191,36,0.08)", padding: "1px 5px", borderRadius: 3 }}>{w}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {/* Multi-Horizon ROI Panel */}
              {r.horizonRank && Object.keys(r.horizonRank).length > 0 && (
                <div style={{ marginTop: 8, padding: "8px 10px", borderRadius: 8, background: "rgba(99,102,241,0.05)", border: "1px solid rgba(99,102,241,0.18)" }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: "#818cf8", letterSpacing: 1, marginBottom: 6 }}>HORIZON ROI ANALYSIS — {Object.keys(r.horizonRank).length} WINDOWS COMPARED</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    {["day","swing","h14","h28","h60"].filter(k => r.horizonRank[k]).map(k => {
                      const h = r.horizonRank[k];
                      const isBest = r.bestHorizon && h.exp === r.bestHorizon.exp;
                      const affordable = h.contract_cost <= (r.price || 9999);
                      const netColor = h.net_roi > 0 ? "var(--green)" : h.net_roi > -10 ? "#fbbf24" : "#f87171";
                      return (
                        <div key={k} style={{
                          display: "flex", alignItems: "center", gap: 6, padding: "4px 7px", borderRadius: 6,
                          background: isBest ? "rgba(99,102,241,0.12)" : "transparent",
                          border: isBest ? "1px solid rgba(99,102,241,0.3)" : "1px solid transparent",
                          opacity: affordable ? 1 : 0.4,
                        }}>
                          <span style={{ fontSize: 9, fontWeight: 700, color: isBest ? "#818cf8" : "var(--dim)", minWidth: 42 }}>{h.label}</span>
                          <span style={{ fontSize: 8, color: "var(--muted)", minWidth: 28 }}>{h.dte}d</span>
                          <span style={{ fontSize: 8, color: "var(--muted)", minWidth: 52 }}>${h.strike}C ${h.mid}</span>
                          <span style={{ fontSize: 8, color: "var(--muted)", minWidth: 38 }}>PoP {h.pop}%</span>
                          <span style={{ fontSize: 8, color: h.ev_pct > 0 ? "var(--green)" : "var(--muted)", minWidth: 40 }}>EV {h.ev_pct > 0 ? "+" : ""}{h.ev_pct}%</span>
                          <span style={{ fontSize: 8, color: "#f87171", minWidth: 38 }}>θ {h.theta_cost_pct}%</span>
                          <span style={{ fontSize: 9, fontWeight: 700, color: netColor, minWidth: 52 }}>net {h.net_roi > 0 ? "+" : ""}{h.net_roi}%</span>
                          <span style={{ fontSize: 7, color: "var(--dim)", marginLeft: "auto" }}>${h.contract_cost}</span>
                          {isBest && <span style={{ fontSize: 7, fontWeight: 800, color: "#818cf8", background: "rgba(99,102,241,0.2)", padding: "1px 5px", borderRadius: 3 }}>BEST</span>}
                          {!affordable && <span style={{ fontSize: 7, color: "#f87171" }}>✗ unaffordable</span>}
                        </div>
                      );
                    })}
                  </div>
                  {r.bestHorizon && (
                    <div style={{ marginTop: 5, fontSize: 9, color: "var(--muted)" }}>
                      Omega selected <strong style={{ color: "#818cf8" }}>{r.bestHorizon.label}</strong> ({r.bestHorizon.dte}d exp, hold ~{r.bestHorizon.hold_days}d) — netROI {r.bestHorizon.net_roi > 0 ? "+" : ""}{r.bestHorizon.net_roi}% · theta cost {r.bestHorizon.theta_cost_pct}%/hold
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Performance Tracker (Self-Learning) */}
      {performance && performance.totalTrades > 0 && (
        <div className="card">
          <div className="sh">Autopilot Performance</div>
          <div style={{ fontSize: 10, color: "var(--muted)", marginBottom: 8 }}>Real trade outcomes — Omega ZI AI also learns in the background using paper simulations to continuously improve.</div>
          <div className="grid4">
            <div className="m-box"><div className="m-lbl">Win Rate</div><div className="m-val pos">{performance.winRate}%</div></div>
            <div className="m-box"><div className="m-lbl">Total P&L</div><div className={`m-val ${performance.totalPnl >= 0 ? "pos" : "neg"}`}>{fmt$(performance.totalPnl)}</div></div>
            <div className="m-box"><div className="m-lbl">Avg P&L</div><div className="m-val">{fmt$(performance.avgPnl)}</div></div>
            <div className="m-box"><div className="m-lbl">Total Trades</div><div className="m-val">{performance.totalTrades}</div></div>
          </div>
          {Object.keys(performance.byClass || {}).length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 500, marginBottom: 6 }}>Win Rate by Zi Class:</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {Object.entries(performance.byClass).map(([cls, d]) => (
                  <div key={cls} style={{ fontSize: 10, color: "var(--muted)" }}>
                    <span style={{ color: ziClassColor(cls), fontWeight: 600 }}>{cls}</span>: {d.winRate}% ({d.trades} trades)
                  </div>
                ))}
              </div>
            </div>
          )}
          {Object.keys(performance.byHorizon || {}).length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 500, marginBottom: 6 }}>Performance by Trade Duration:</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: 6 }}>
                {["Day","Swing","14-Day","28-Day","60-Day","Unknown"].filter(h => performance.byHorizon[h]).map(h => {
                  const d = performance.byHorizon[h];
                  return (
                    <div key={h} style={{ background: "var(--layer2)", borderRadius: 8, padding: "8px 10px", border: `1px solid ${d.winRate >= 60 ? "rgba(74,222,128,0.2)" : d.winRate >= 40 ? "var(--border)" : "rgba(248,113,113,0.15)"}` }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: "var(--dim)", letterSpacing: 1, marginBottom: 4 }}>{h.toUpperCase()}</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: d.winRate >= 60 ? "var(--green)" : d.winRate >= 40 ? "#fbbf24" : "var(--red)" }}>{d.winRate}%</div>
                      <div style={{ fontSize: 9, color: "var(--muted)" }}>{d.trades} trades · {d.wins}W</div>
                      <div style={{ fontSize: 9, color: d.pnl >= 0 ? "var(--green)" : "var(--red)" }}>{d.pnl >= 0 ? "+" : ""}${d.pnl.toFixed(0)} P&L</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {previewTrade && (
        <RealTradePreviewModal trade={previewTrade} onClose={() => setPreviewTrade(null)} />
      )}
    </div>
  );
}

export default AutopilotPanel;
